import * as React from "react";
import { cn } from "../../../lib/utils";

/**
 * Switch Component - Drop-in replacement for antd Switch
 *
 * @example
 * <Switch
 *   checked={isEnabled}
 *   onChange={(checked) => setIsEnabled(checked)}
 * />
 */
const Switch = React.forwardRef(({
  checked = false,
  onChange,
  disabled = false,
  className,
  size = "default",
  ...props
}, ref) => {
  const handleChange = () => {
    if (!disabled) {
      onChange?.(!checked);
    }
  };

  const sizeClasses = {
    sm: "h-5 w-9",
    default: "h-6 w-11",
    lg: "h-7 w-14",
  };

  const thumbSizeClasses = {
    sm: "h-4 w-4",
    default: "h-5 w-5",
    lg: "h-6 w-6",
  };

  const thumbTranslateClasses = {
    sm: checked ? "translate-x-4" : "translate-x-0.5",
    default: checked ? "translate-x-5" : "translate-x-0.5",
    lg: checked ? "translate-x-7" : "translate-x-0.5",
  };

  return (
    <button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={handleChange}
      className={cn(
        "relative inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 focus-visible:ring-offset-2",
        sizeClasses[size] || sizeClasses.default,
        checked
          ? "bg-yellow-400"
          : "bg-gray-300 dark:bg-gray-600",
        disabled && "opacity-50 cursor-not-allowed",
        className
      )}
      {...props}
    >
      <span
        className={cn(
          "pointer-events-none inline-block rounded-full bg-white shadow-lg ring-0 transition-transform duration-200 ease-in-out",
          thumbSizeClasses[size] || thumbSizeClasses.default,
          thumbTranslateClasses[size] || thumbTranslateClasses.default
        )}
      />
    </button>
  );
});

Switch.displayName = "Switch";

export { Switch };
